import { safeJson } from "@/lib/http";
import { supabase } from "@/lib/supabase";
import { normContexte } from "@/lib/checklist";

export const TYPES_DOSSIER = [
    { value: "audit_financier", label: "Audit financier" },
    { value: "restructuration", label: "Restructuration" },
    { value: "diagnostic_flash", label: "Diagnostic flash" },
    { value: "plan_de_continuation", label: "Plan de continuation" },
    { value: "due_diligence", label: "Due diligence" },
];

// Le SIREN synthétique « IMM… » identifie une entreprise en cours d'immatriculation (sans SIREN attribué).
export const isImmatriculation = (siren) => typeof siren === "string" && siren.startsWith("IMM");

const REPORT_TYPES_IN_BONIS = [
    { value: "audit_financier", label: "Audit financier" },
    { value: "due_diligence", label: "Analyse Due Diligence" },
    { value: "valorisation", label: "Valorisation d'entreprise" },
    { value: "synthese_creation", label: "Synthèse de Création" },
    { value: "business_plan", label: "Business Plan Narratif" },
];

/** Types de rapport proposés selon le contexte : liste restructuration complète, ou options « In Bonis » adaptées. */
export function reportTypesForContexte(contexte) {
    return normContexte(contexte) === "restructuration" ? TYPES_DOSSIER : REPORT_TYPES_IN_BONIS;
}

export const STATUTS = {
    en_attente_documents: "En attente de documents",
    pret_pour_analyse: "Prêt pour analyse",
    en_cours: "En cours",
    clos: "Clos",
};

export const TYPES_PROFESSIONNEL = [
    "Expert-comptable",
    "Avocat",
    "Dirigeant",
    "Mandataire judiciaire",
    "Autre",
];

export function labelType(v) {
    return TYPES_DOSSIER.find((t) => t.value === v)?.label ?? v;
}

export const RISQUES = ["Non évalué", "Faible", "Vigilance", "Élevé"];

export async function updateRisque(id, niveau_risque, source = "manuel") {
    const { error } = await supabase.from("dossiers").update({ niveau_risque, niveau_risque_source: source }).eq("id", id);
    if (error) throw error;
}

export async function fetchDossiers() {
    const { data, error } = await supabase
        .from("dossiers")
        .select(
            "id, nom_mission, type_dossier, statut_dossier, niveau_risque, niveau_risque_source, created_at, entreprises(siren, nom_entreprise), users_clients(prenom_nom, email)"
        )
        .order("created_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
}

export async function fetchDossier(id) {
    const REL = "entreprises(siren, nom_entreprise, forme_juridique, nom_gerant, adresse), users_clients(id, prenom_nom, email, type_professionnel, nom_cabinet, created_at), documents_dataroom(id, type_document), kpis_financiers(id)";
    const BASE = "id, nom_mission, type_dossier, statut_dossier, niveau_risque, niveau_risque_source, created_at, notes, exercices_fiscaux, contexte_audit, contexte_autre";
    const q = (cols) => supabase.from("dossiers").select(cols).eq("id", id).maybeSingle();
    // Tolérant à l'absence de la migration Phase B : repli sans documents_requis si la colonne n'existe pas encore.
    let { data, error } = await q(`${BASE}, documents_requis, ${REL}`);
    if (error) ({ data, error } = await q(`${BASE}, ${REL}`));
    if (error) throw error;
    return data;
}

export async function updateStatut(id, statut_dossier) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Session expirée, reconnectez-vous.");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dossiers/${id}/statut`, {
        method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ statut_dossier }),
    });
    const j = await safeJson(r);
    if (!r.ok) throw new Error(typeof j.detail === "string" ? j.detail : `Erreur ${r.status}`);
    return j;
}

export async function updateContexte(id, contexte_audit, contexte_autre) {    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Session expirée, reconnectez-vous.");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dossiers/${id}/contexte`, {
        method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ contexte_audit, contexte_autre: contexte_autre || null }),
    });
    const j = await safeJson(r);
    if (!r.ok) throw new Error(typeof j.detail === "string" ? j.detail : `Erreur ${r.status}`);
    return j;
}

// SUPER-LOT 103 Phase B — met à jour la liste des pièces requises (JSONB) du dossier.
export async function updateDocumentsRequis(id, documents_requis) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Session expirée, reconnectez-vous.");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dossiers/${id}/documents-requis`, {
        method: "PATCH", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ documents_requis: documents_requis ?? [] }),
    });
    const j = await safeJson(r);
    if (!r.ok) throw new Error(typeof j.detail === "string" ? j.detail : `Erreur ${r.status}`);
    return j;
}

export async function dupliquerDossier(id, payload) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Session expirée, reconnectez-vous.");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dossiers/${id}/dupliquer`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(payload),
    });
    const j = await safeJson(r);
    if (!r.ok) throw new Error(typeof j.detail === "string" ? j.detail : `Erreur ${r.status}`);
    return j;
}

export async function envoyerRelance(id, message_personnalise) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Session expirée, reconnectez-vous.");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dossiers/${id}/relance`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ message_personnalise: message_personnalise || null }),
    });
    const j = await safeJson(r);
    if (!r.ok) throw new Error(typeof j.detail === "string" ? j.detail : `Erreur ${r.status}`);
    return j;
}

export async function fetchAlertes(id) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dossiers/${id}/alertes`, { headers: { Authorization: `Bearer ${token}` } });
    const j = await safeJson(r);
    if (!r.ok) throw new Error(j.detail || `Erreur ${r.status}`);
    return j;
}

export async function fetchAltares(siren) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/altares/${siren}`, { headers: { Authorization: `Bearer ${token}` } });
    const j = await safeJson(r);
    if (!r.ok) throw new Error(j.detail || `Erreur ${r.status}`);
    return j;
}

export async function fetchClients() {
    const [{ data, error }, { data: liens }] = await Promise.all([
        supabase.from("users_clients").select("id, prenom_nom, email, type_professionnel, nom_cabinet").order("prenom_nom"),
        supabase.from("dossiers").select("client_id, entreprise_siren, created_at, entreprises(siren, nom_entreprise)").order("created_at", { ascending: false }),
    ]);
    if (error) throw error;
    const byClient = {};
    for (const l of liens ?? []) {
        if (!l.entreprises) continue;
        const list = (byClient[l.client_id] ||= []);
        if (!list.some((e) => e.siren === l.entreprises.siren)) list.push(l.entreprises);
    }
    return (data ?? []).map((c) => ({ ...c, entreprises: byClient[c.id] ?? [] }));
}

export function labelClient(c) {
    const ent = c.entreprises?.[0]?.nom_entreprise || c.nom_cabinet;
    return ent ? `${ent} — ${c.prenom_nom}` : c.prenom_nom;
}

export async function fetchEntrepriseBySiren(siren) {
    const { data, error } = await supabase
        .from("entreprises")
        .select("siren, nom_entreprise, forme_juridique, nom_gerant, adresse")
        .eq("siren", siren)
        .maybeSingle();
    if (error) throw error;
    return data;
}

export async function createDossier({ nom_mission, client, entreprise, type_dossier, notes, exercices_fiscaux, contexte_audit, contexte_autre, documents_requis }) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error("Session expirée, reconnectez-vous.");
    const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dossiers`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
            nom_mission,
            client: client.id ? { id: client.id } : { email: client.email, prenom_nom: client.prenom_nom, type_professionnel: client.type_professionnel || null, nom_cabinet: client.nom_cabinet || null },
            entreprise: {
                siren: entreprise.siren, nom_entreprise: entreprise.nom_entreprise, forme_juridique: entreprise.forme_juridique || null,
                nom_gerant: entreprise.nom_gerant || null, adresse: entreprise.adresse || null, en_immatriculation: !!entreprise.en_immatriculation,
            },
            type_dossier, notes, exercices_fiscaux: exercices_fiscaux ?? [], contexte_audit, contexte_autre: contexte_autre || null,
            documents_requis: documents_requis ?? null,
        }),
    });
    const j = await safeJson(r);
    if (!r.ok) throw new Error(j.detail || `Erreur ${r.status}`);
    return j;
}
